/**
 * Generates the three parallax layers for an environment as textures.
 *
 * Everything is produced under the same `backgrounds/{env}/{layer}` asset keys
 * the manifest uses, so Prompt 10's SVGs replace these without touching the
 * renderer. The near layer is drawn to tile horizontally: its left and right
 * edges match, so a TileSprite repeats it seamlessly.
 */
import Phaser from 'phaser';
import { backgroundLayerKey, paletteFor } from '../data/themes';
import type { EnvironmentPalette, GradientStops } from '../data/themes';
import { GAME_HEIGHT, GAME_WIDTH } from '../types';
import type { BackgroundTheme } from '../types';

export type LayerName = 'sky' | 'far' | 'near';

/** Near-layer tiles are a screen wide, so one repeat covers the stage. */
/**
 * The sky is generated taller than the screen so it can be panned as the player
 * climbs. Generated at size rather than scaled up: stretching a screen-sized
 * texture turns the sun into an oval.
 */
export const SKY_OVERSCAN = 1.5;
export const SKY_TEXTURE_HEIGHT = Math.round(GAME_HEIGHT * SKY_OVERSCAN);

export const NEAR_TILE_WIDTH = GAME_WIDTH;
export const NEAR_TILE_HEIGHT = 260;
export const FAR_LAYER_HEIGHT = 420;

/**
 * The cave closes in from the sides over the whole screen, so its far layer is
 * full height. Everything else is a ridge line sitting on the horizon, and a
 * short layer leaves more sky visible.
 */
export function farLayerHeightFor(env: BackgroundTheme): number {
  return env === 'ice-cave' ? GAME_HEIGHT : FAR_LAYER_HEIGHT;
}

/** Screen y where the far layer is pinned. */
export function farLayerYFor(env: BackgroundTheme): number {
  return env === 'ice-cave' ? 0 : GAME_HEIGHT - FAR_LAYER_HEIGHT + 56;
}

type Gfx = Phaser.GameObjects.Graphics;

function sample(stops: GradientStops, t: number): number {
  const first = stops[0];
  if (first === undefined) return 0x000000;
  if (t <= first[1]) return first[0];
  for (let i = 1; i < stops.length; i += 1) {
    const previous = stops[i - 1];
    const current = stops[i];
    if (previous === undefined || current === undefined) continue;
    if (t <= current[1]) {
      const span = current[1] - previous[1];
      const local = span <= 0 ? 0 : (t - previous[1]) / span;
      const blended = Phaser.Display.Color.Interpolate.ColorWithColor(
        Phaser.Display.Color.ValueToColor(previous[0]),
        Phaser.Display.Color.ValueToColor(current[0]),
        100,
        Math.round(local * 100),
      );
      return Phaser.Display.Color.GetColor(blended.r, blended.g, blended.b);
    }
  }
  return stops[stops.length - 1]?.[0] ?? 0x000000;
}

/* ------------------------------------------------------------------- sky */

function drawSky(gfx: Gfx, palette: EnvironmentPalette): void {
  const height = SKY_TEXTURE_HEIGHT;
  const bands = 140;
  const bandHeight = height / bands;
  for (let i = 0; i < bands; i += 1) {
    gfx.fillStyle(sample(palette.sky, i / (bands - 1)), 1);
    gfx.fillRect(0, i * bandHeight, GAME_WIDTH, bandHeight + 1);
  }

  const sun = palette.sun;
  if (sun === null) return;

  // Many faint rings rather than a few strong ones: five reads as a bullseye,
  // eighteen reads as a glow.
  const cx = GAME_WIDTH * 0.74;
  const cy = height * sun.y;
  const rings = 44;
  for (let ring = rings; ring >= 1; ring -= 1) {
    const t = ring / rings;
    gfx.fillStyle(sun.glow, 0.042 * (1 - t) + 0.009);
    gfx.fillCircle(cx, cy, 90 + t * 250);
  }
  gfx.fillStyle(sun.color, 1);
  gfx.fillCircle(cx, cy, 88);
}

/* ------------------------------------------------------------------- far */

/** Distant ice shelves and mountains, as a silhouette strip. */
function drawFar(gfx: Gfx, palette: EnvironmentPalette, env: BackgroundTheme): void {
  const base = FAR_LAYER_HEIGHT;

  if (env === 'ice-cave') {
    // Walls run the full height and pinch towards the middle, so there is no
    // hard edge where the layer starts.
    gfx.fillStyle(palette.far, 1);
    gfx.beginPath();
    gfx.moveTo(0, 0);
    gfx.lineTo(300, 0);
    gfx.lineTo(170, base * 0.42);
    gfx.lineTo(320, base * 0.72);
    gfx.lineTo(210, base);
    gfx.lineTo(0, base);
    gfx.closePath();
    gfx.fillPath();
    gfx.beginPath();
    gfx.moveTo(GAME_WIDTH, 0);
    gfx.lineTo(GAME_WIDTH - 300, 0);
    gfx.lineTo(GAME_WIDTH - 160, base * 0.38);
    gfx.lineTo(GAME_WIDTH - 330, base * 0.68);
    gfx.lineTo(GAME_WIDTH - 220, base);
    gfx.lineTo(GAME_WIDTH, base);
    gfx.closePath();
    gfx.fillPath();

    // Icicles hang from the roof of the cave.
    gfx.fillStyle(palette.farAccent, 0.85);
    for (let i = 0; i < 11; i += 1) {
      const x = 60 + i * 118;
      const length = 44 + ((i * 37) % 80);
      gfx.fillTriangle(x - 13, 0, x + 13, 0, x, length);
    }
    return;
  }

  // A ridge line of peaks, taller towards the middle.
  const peaks = 7;
  gfx.fillStyle(palette.far, 1);
  gfx.beginPath();
  gfx.moveTo(0, base);
  for (let i = 0; i <= peaks; i += 1) {
    const x = (GAME_WIDTH / peaks) * i;
    const centreBias = 1 - Math.abs(i / peaks - 0.5) * 1.1;
    const height = base * (0.3 + centreBias * 0.5);
    gfx.lineTo(x - GAME_WIDTH / peaks / 2, base - height * 0.55);
    gfx.lineTo(x, base - height);
  }
  gfx.lineTo(GAME_WIDTH, base);
  gfx.closePath();
  gfx.fillPath();

  // Snow caps.
  gfx.fillStyle(palette.farAccent, 0.9);
  for (let i = 0; i <= peaks; i += 1) {
    const x = (GAME_WIDTH / peaks) * i;
    const centreBias = 1 - Math.abs(i / peaks - 0.5) * 1.1;
    const height = base * (0.3 + centreBias * 0.5);
    const capDrop = height * 0.22;
    gfx.fillTriangle(
      x,
      base - height,
      x + capDrop * 0.9,
      base - height + capDrop,
      x - capDrop * 0.9,
      base - height + capDrop,
    );
  }
}

/* ------------------------------------------------------------------ near */

/**
 * Foreground drifts. Drawn so the left and right edges meet: the first and last
 * control points share a height, which is what lets a TileSprite loop it.
 */
function drawNear(gfx: Gfx, palette: EnvironmentPalette, env: BackgroundTheme): void {
  const h = NEAR_TILE_HEIGHT;
  const w = NEAR_TILE_WIDTH;

  gfx.fillStyle(palette.near, 1);
  gfx.beginPath();
  gfx.moveTo(0, h);
  gfx.lineTo(0, h * 0.55);
  const humps = 4;
  for (let i = 0; i < humps; i += 1) {
    const x0 = (w / humps) * i;
    const x1 = (w / humps) * (i + 1);
    const peak = h * (i % 2 === 0 ? 0.26 : 0.4);
    gfx.lineTo(x0 + (x1 - x0) * 0.5, peak);
    gfx.lineTo(x1, h * 0.55);
  }
  gfx.lineTo(w, h);
  gfx.closePath();
  gfx.fillPath();

  // A lighter crest along the top of the drifts.
  gfx.lineStyle(6, palette.nearAccent, 0.55);
  gfx.beginPath();
  gfx.moveTo(0, h * 0.55);
  for (let i = 0; i < humps; i += 1) {
    const x0 = (w / humps) * i;
    const x1 = (w / humps) * (i + 1);
    const peak = h * (i % 2 === 0 ? 0.26 : 0.4);
    gfx.lineTo(x0 + (x1 - x0) * 0.5, peak);
    gfx.lineTo(x1, h * 0.55);
  }
  gfx.strokePath();

  if (env === 'sunset') {
    // A couple of distant penguin silhouettes on the drifts (CLAUDE.md).
    gfx.fillStyle(0x24143a, 1);
    for (const [x, scale] of [
      [w * 0.2, 1],
      [w * 0.28, 0.72],
      [w * 0.76, 0.86],
    ] as const) {
      const bodyH = 46 * scale;
      const bodyW = 24 * scale;
      const footY = h * 0.52;
      gfx.fillEllipse(x, footY - bodyH / 2, bodyW, bodyH);
      gfx.fillCircle(x, footY - bodyH, bodyW * 0.44);
    }
  }

  if (env === 'research-station') {
    // Hut silhouettes with lit windows.
    for (const x of [w * 0.18, w * 0.62]) {
      gfx.fillStyle(0x16233a, 1);
      gfx.fillRect(x - 60, h * 0.3, 120, 70);
      gfx.fillTriangle(x - 72, h * 0.3, x + 72, h * 0.3, x, h * 0.3 - 38);
      gfx.fillStyle(palette.nearAccent, 0.95);
      gfx.fillRect(x - 34, h * 0.34, 26, 22);
      gfx.fillRect(x + 10, h * 0.34, 26, 22);
    }
  }
}

/* -------------------------------------------------------------- building */

function generate(
  scene: Phaser.Scene,
  key: string,
  width: number,
  height: number,
  draw: (gfx: Gfx) => void,
): void {
  if (scene.textures.exists(key)) return;
  const gfx = scene.make.graphics({ x: 0, y: 0 }, false);
  draw(gfx);
  gfx.generateTexture(key, width, height);
  gfx.destroy();
}

/** Builds any of the environment's layers that are not already textures. */
export function ensureEnvironmentTextures(scene: Phaser.Scene, env: BackgroundTheme): void {
  const palette = paletteFor(env);
  generate(scene, backgroundLayerKey(env, 'sky'), GAME_WIDTH, SKY_TEXTURE_HEIGHT, (gfx) =>
    drawSky(gfx, palette),
  );
  generate(scene, backgroundLayerKey(env, 'far'), GAME_WIDTH, FAR_LAYER_HEIGHT, (gfx) =>
    drawFar(gfx, palette, env),
  );
  generate(scene, backgroundLayerKey(env, 'near'), NEAR_TILE_WIDTH, NEAR_TILE_HEIGHT, (gfx) =>
    drawNear(gfx, palette, env),
  );
}

/** Aurora ribbons are drawn live rather than baked, so they can drift. */
export const AURORA_RIBBON_KEY = 'fx/aurora-ribbon';

export function ensureAuroraTexture(scene: Phaser.Scene): void {
  generate(scene, AURORA_RIBBON_KEY, 512, 200, (gfx) => {
    // Built from thin wavy strips whose alpha fades out downwards. Filling a
    // solid block below the wave leaves a hard edge once it is blended in.
    const rows = 54;
    for (let i = 0; i < rows; i += 1) {
      const t = i / (rows - 1);
      const fadeIn = t < 0.12 ? t / 0.12 : 1;
      const alpha = 0.17 * (1 - t) * (1 - t) * fadeIn;
      gfx.fillStyle(0xffffff, alpha);
      // Strips overlap horizontally so no seams show between them.
      for (let x = 0; x <= 512; x += 6) {
        const wave = Math.sin((x / 512) * Math.PI * 2 + t * 1.1) * 24;
        gfx.fillRect(x, 26 + wave + i * 3, 9, 4.2);
      }
    }
  });
}
